import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { 
  User, Users, MessageCircle, Smile, Heart, Star, 
  Clapperboard, Shirt, Wallet, Video, Eye, Save, 
  MoreVertical, Calendar, CheckCircle2 
} from 'lucide-react';
import { format as formatFns, isValid } from 'date-fns';

// ✅ Added fetchPublicRoleListApi import to get resolved project poster URLs and types
import { fetchRoleByIdApi, fetchPublicRoleListApi } from '../../services/api';
import './RoleRequirement.css';

// Live Unsplash placeholder fallback
const defaultPoster = "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=500&auto=format&fit=crop&q=60";
const defaultProfile = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=60";

// Helper: Format Semicolon Separated Database strings to Comma Separated for UI
const formatSemicolonString = (str, fallback = '---') => {
  if (!str) return fallback;
  return String(str)
    .split(';')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, char => char.toUpperCase())) // CamelCase to TitleCase
    .join(', ') || fallback;
};

// Map talent names to specific emojis dynamically
const getSkillIcon = (skillName) => {
    if (!skillName) return '⭐';
    const name = skillName.toLowerCase().trim();
    if (name.includes('acting') || name.includes('actor') || name.includes('actress')) return '🎭';
    if (name.includes('dance') || name.includes('dancing') || name.includes('dancer')) return '💃';
    if (name.includes('sing') || name.includes('singing') || name.includes('singer')) return '🎤';
    if (name.includes('voice over') || name.includes('voiceover') || name.includes('voice-over') || name.includes('dubbing')) return '🎙️';
    if (name.includes('swim') || name.includes('swimming')) return '🏊';
    if (name.includes('horse') || name.includes('riding')) return '🏇';
    if (name.includes('yoga')) return '🧘';
    if (name.includes('drive') || name.includes('driving')) return '🚗';
    if (name.includes('gym') || name.includes('fitness') || name.includes('workout')) return '🏋️';
    if (name.includes('cricket')) return '🏏';
    if (name.includes('badminton')) return '🏸';
    if (name.includes('cook') || name.includes('cooking') || name.includes('chef')) return '🍳';
    return '⭐';
};

// SAFE DYNAMIC AI SCORES CALCULATOR (Zero undefined error risk)
const calculateAIScores = (tp) => {
    const requiredTalentStr = tp.required_talent ? String(tp.required_talent) : '';
    const requiredTalentLower = requiredTalentStr.toLowerCase();
    const talentsCount = requiredTalentStr ? requiredTalentStr.split(';').length : 0;
    
    const hasBio = tp.role_description && typeof tp.role_description === 'string' && tp.role_description.length > 10;
    const bioLength = tp.role_description ? tp.role_description.length : 0;
    const experiencesCount = Array.isArray(tp.experiences) ? tp.experiences.length : 0;
    const educationsCount = Array.isArray(tp.educations) ? tp.educations.length : 0;
    const languagesCount = tp.required_language ? String(tp.required_language).split(';').length : 1;
    const hasImage = !!tp.project_image;

    let emotion = 74;
    if (requiredTalentLower.includes('acting')) emotion += 6;
    emotion += Math.min(educationsCount * 4, 12);
    
    let voice = 72;
    if (requiredTalentLower.includes('sing') || requiredTalentLower.includes('voice')) voice += 8;
    voice += Math.min(languagesCount * 3, 12);

    let dialogue = 70;
    if (hasBio) dialogue += Math.min(Math.floor(bioLength / 50) * 2, 10);
    dialogue += Math.min(languagesCount * 2, 8);
    dialogue += Math.min(educationsCount * 3, 9);

    let confidence = 68;
    if (hasBio) confidence += Math.min(Math.floor(bioLength / 40) * 2, 12);
    confidence += Math.min(experiencesCount * 4, 16);

    let body = 74;
    if (tp.required_height) body += 6;
    body += Math.min(experiencesCount * 3, 15);

    let camera = 65;
    if (hasImage) camera += 15;
    camera += Math.min(experiencesCount * 4, 16);

    let pace = 72;
    pace += Math.min(experiencesCount * 3, 12);
    pace += Math.min(educationsCount * 4, 12);

    let versatility = 60;
    versatility += Math.min(talentsCount * 6, 24);
    versatility += Math.min(languagesCount * 4, 12);

    const cap = (score) => Math.min(Math.max(score, 50), 98);

    const emotionFinal = cap(emotion);
    const voiceFinal = cap(voice);
    const dialogueFinal = cap(dialogue);
    const confidenceFinal = cap(confidence);
    const bodyFinal = cap(body);
    const cameraFinal = cap(camera);
    const paceFinal = cap(pace);
    const versatilityFinal = cap(versatility);

    const overall = Math.round(
        (emotionFinal + voiceFinal + dialogueFinal + confidenceFinal + bodyFinal + cameraFinal + paceFinal + versatilityFinal) / 8
    );

    let performanceLabel = "Rising Performer (Static)";
    if (overall >= 90) performanceLabel = "Excellent Performer (Static)";
    else if (overall >= 80) performanceLabel = "Great Performer (Static)";
    else if (overall >= 70) performanceLabel = "Good Performer (Static)";

    return {
        emotion: emotionFinal,
        voice: voiceFinal,
        dialogue: dialogueFinal,
        confidence: confidenceFinal,
        body: bodyFinal,
        camera: cameraFinal,
        pace: paceFinal,
        versatility: versatilityFinal,
        overall,
        label: performanceLabel
    };
};

export default function RoleRequirement() {
  const { id } = useParams(); // Extract dynamic localRoleId from URL parameters
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // ✅ Combined dynamic project details state loaded from public API
  const [projectMeta, setProjectMeta] = useState({
      imageUrl: null,
      projectType: null,
      language: null,
      director: null
  });

  // ✅ LOAD DYNAMIC DATA ON MOUNT
  const loadRoleData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      // 1. Fetch main role fields
      const res = await fetchRoleByIdApi(id); 
      const fetchedData = res?.data?.data || res?.data;
      if (fetchedData) {
        setRole(fetchedData);
      } else {
        setError("Role requirement details not found.");
        setLoading(false);
        return;
      }

      // 2. 👈 ✅ Fetch public roles list to find matching poster, type, languages, and director info
      try {
        const publicRes = await fetchPublicRoleListApi();
        const publicRoles = publicRes?.data?.data || publicRes?.data || [];
        const matched = publicRoles.find(r => 
          String(r.id) === String(id) || 
          String(r.jogetRoleId) === String(id) || 
          String(r.localRoleId) === String(id)
        );
        if (matched) {
          setProjectMeta({
              imageUrl: matched.projectImageUrl || null,
              projectType: matched.projectType || matched.project_type || null,
              language: matched.required_language || matched.required_language || null,
              director: matched.director_name || matched.directorName || null
          });
        }
      } catch (imgErr) {
        console.warn("Background project image lookup failed:", imgErr.message);
      }

    } catch (err) {
      console.error("Error loading role requirement details:", err);
      setError("Failed to load dynamic role requirements from database.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRoleData();
  }, [loadRoleData]);

  // ✅ LOADING VIEW
  if (loading) return (
    <div className="text-center p-5 rr-main-container d-flex flex-column align-items-center justify-content-center text-white" style={{ minHeight: '100vh' }}>
      <Spinner animation="border" variant="danger" />
      <p className="mt-3">Analyzing and loading role requirements...</p>
    </div>
  );

  // ✅ ERROR VIEW
  if (error) return (
    <div className="rr-main-container p-4" style={{ minHeight: '100vh', background: '#0b0f19' }}>
      <Alert variant="danger" className="bg-dark text-danger border-danger">{error}</Alert>
    </div>
  );

  // ====================================================
  // ✅ DATA BINDING WITH FALLBACKS
  // ====================================================
  const roleData = role || {};
  
  // Parse original request_data if some meta fields are missing in core db
  let req = {};
  if (roleData.request_data) {
    try {
      req = typeof roleData.request_data === 'string'
        ? JSON.parse(roleData.request_data)
        : roleData.request_data;
    } catch (e) {
      console.error("Failed to parse request_data");
    }
  }
  if (!req || typeof req !== 'object') req = {};

  // Formatted basic requirements from DB Column mappings
  const gender = formatSemicolonString(roleData.required_gender, 'Female');
  const playingAge = formatSemicolonString(roleData.required_age, '23 - 28 Years');
  const height = formatSemicolonString(roleData.required_height, "5'4\" - 5'7\"");
  const bodyType = formatSemicolonString(roleData.required_body_type, 'Slim / Athletic');
  const skinTone = formatSemicolonString(roleData.required_skin_color, 'Any');
  const hair = formatSemicolonString(roleData.required_hair_type, 'Medium to Long');
  const experience = formatSemicolonString(roleData.required_experience_level, 'Freshers Welcome');
  const location = roleData.shoot_location || req.shootLocation || 'Mumbai';

  // Badges & Traits
  const lookTags = roleData.role_category ? String(roleData.role_category).split(';') : ['Modern', 'Urban', 'Corporate'];
  const lookStyles = req.lookStyles ? (Array.isArray(req.lookStyles) ? req.lookStyles : String(req.lookStyles).split(',')) : ['Confident Look', 'Smart', 'Elegant', 'Professional', 'Natural Beauty'];
  const personalityTraits = req.personalityTraits && Array.isArray(req.personalityTraits) ? req.personalityTraits : ['Confident', 'Intelligent', 'Ambitious', 'Strong-Willed', 'Emotional', 'Independent', 'Compassionate', 'Determined'];

  // Emotions (Importance levels)
  const emotions = req.emotions && Array.isArray(req.emotions) ? req.emotions : [
    { e: '👵', label: 'Confidence (Static)', val: 95 },
    { e: '😡', label: 'Anger (Static)', val: 80 },
    { e: '😢', label: 'Sadness (Static)', val: 70 },
    { e: '🎯', label: 'Determination (Static)', val: 90 },
    { e: '🥺', label: 'Vulnerability (Static)', val: 75 },
    { e: '💖', label: 'Love / Romance (Static)', val: 50 },
    { e: '😊', label: 'Joy (Static)', val: 30 },
    { e: '😨', label: 'Fear (Static)', val: 20 },
  ];

  // Skills required
  const skillsRequired = req.skillsRequired && Array.isArray(skillsRequired) ? req.skillsRequired : [
    { s: 'Acting (Static)', r: 5 },
    { s: 'Dialogue Delivery (Static)', r: 5 },
    { s: 'Emotional Acting (Static)', r: 5 },
    { s: 'Courtroom Acting (Static)', r: 5 },
    { s: 'Expressions (Static)', r: 5 },
    { s: 'Body Language (Static)', r: 4 },
    { s: 'Voice Modulation (Static)', r: 5 },
    { s: 'Dance (Static)', r: 4 },
    { s: 'Singing (Static)', r: 2 },
    { s: 'Other (Specify) (Static)', r: 1 },
  ];

  // Schedules (Format Dates from first available schedule)
  const schedules = Array.isArray(roleData.schedules) ? roleData.schedules : [];
  const firstSchedule = schedules[0] || {};
  
  const formatDate = (dateVal) => {
    if (!dateVal) return '---';
    const d = new Date(dateVal);
    return isValid(d) ? formatFns(d, 'dd MMM yyyy') : '---';
  };

  const formattedStartDate = formatDate(firstSchedule.startDate || roleData.projectStartDate);
  const formattedEndDate = formatDate(firstSchedule.endDate || roleData.projectEndDate || roleData.end_date);

  const ai = calculateAIScores(roleData);

  return (
    <div className="container-fluid py-4 px-xl-5 rr-main-container text-white">

      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center rr-top-header pb-3 mb-4">
        <div>
          <h3 className="fw-bold mb-0 text-white">ROLE REQUIREMENT</h3>
          <p className="text-muted mb-0 rr-header-sub">Create detailed role requirement to get the best matching candidates</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {/* ✅ Changed to 'rr-preview-btn' to fix hover invisibility issue */}
          <button className="btn rr-action-btn rr-preview-btn"><Eye size={16} /> Preview Role</button>
          <button className="btn btn-danger text-white fw-bold rr-action-btn" onClick={() => navigate(`/dashboard/post-roles/${id}`)}><Save size={16} /> Edit Role</button>
          <button className="btn btn-outline-light text-white p-2"><MoreVertical size={18} /></button>
        </div>
      </div>

      {/* BANNER DETAIL CARD */}
      <div className="movie-card-header rr-banner-card d-flex flex-column flex-md-row gap-4 mb-4">

        <div className="rr-poster-wrapper">
          <img 
            src={projectMeta.imageUrl || roleData.projectImageUrl || roleData.project?.project_image || roleData.project_image || req.projectImage || defaultPoster} // ✅ Now fetches associated project's dynamic image first
            alt="Movie Poster" 
            className="poster-image rr-poster-img"
          />
        </div>

        <div className="flex-grow-1">
          <ul className="details-list rr-details-list">
            <li>
              <span className="label">Project / Show Title</span>
              <span className="value fw-bold text-white">{roleData.projectName || req.projectName || '---'}</span>
            </li>
            <li>
              <span className="label">Production House</span>
              <span className="value">{req.productionHouse || 'Dreamscape Productions (Static)'}</span>
            </li>
            <li>
              <span className="label">Project Type</span>
              {/* 👈 ✅ Project Type is now fully dynamic from the public roles listing */}
              <span className="value">{formatSemicolonString(roleData.project_type || projectMeta.projectType || req.projectType, '--- (Static)')}</span>
            </li>
            <li>
              <span className="label">Language</span>
              <span className="value">{formatSemicolonString(roleData.required_language || projectMeta.language || req.programLanguage, '--- (Static)')}</span>
            </li>
            <li>
              <span className="label">Director</span>
              <span className="value">{roleData.director_name || projectMeta.director || req.directorName || '--- (Static)'}</span>
            </li>
          </ul>
        </div>

        <div className="flex-grow-1">
          <ul className="details-list rr-details-list">
            <li>
              <span className="label">Role Name</span>
              <span className="value fw-bold text-white fs-5">{roleData.role_name || req.roleName || '---'}</span>
            </li>
            <li>
              <span className="label">Character Type</span>
              <span className="value">
                <span className="badge badge-red badge-custom">{formatSemicolonString(roleData.role_category || req.characterType, '---')}</span>
              </span>
            </li>
            
            <li>
              <span className="label">Shoot Location</span>
              <span className="value">{location}</span>
            </li>
            <li>
              <span className="label">Casting Director</span>
              <span className="value">{req.castingDirector || '--- (Static)'}</span>
            </li>
          </ul>
        </div>

        <div className="flex-grow-1 rr-summary-column">
          <div className="text-muted mb-1 rr-label-text">Role Summary</div>
          <p className="rr-summary-paragraph">
            {roleData.role_description || req.roleSummary || '---'}
          </p>
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="text-muted" style={{ fontSize: '0.9rem' }}>Role Priority</span>
            <span className="badge badge-red badge-custom">{req.rolePriority || 'High (Static)'}</span>
          </div>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            <Calendar size={14} className="me-1"/> Audition Last Date
            <div className="text-white mt-1 fw-bold">{roleData.end_date || req.auditionLastDate || '---'}</div>
          </div>
        </div>
      </div>

      <div className="row g-4">

        {/* ================= LEFT COLUMN ================= */}
        <div className="col-12 col-lg-4 d-flex flex-column gap-4">

          <div className="card-dark">
            <div className="section-title">
              <User size={18} className="card-header-icon text-primary me-2" />
              1. BASIC REQUIREMENTS
            </div>
            <ul className="details-list rr-column-list">
              <li><span className="label">Gender</span><span className="value text-white">{gender}</span></li>
              <li><span className="label">Playing Age</span><span className="value text-white">{playingAge}</span></li>
              <li><span className="label">Height</span><span className="value text-white">{height}</span></li>
              <li><span className="label">Body Type</span><span className="value text-white">{bodyType}</span></li>
              <li><span className="label">Skin Tone</span><span className="value text-white">{skinTone}</span></li>
              <li><span className="label">Hair</span><span className="value text-white">{hair}</span></li>
              <li><span className="label">Experience</span><span className="value text-white">{experience}</span></li>
              
              <li><span className="label">Location</span><span className="value text-white">{formatSemicolonString(location)}</span></li>
             
            </ul>
          </div>

          <div className="card-dark">
            <div className="section-title">
              <Smile size={18} className="card-header-icon text-primary me-2" />
              4. CHARACTER & PERSONALITY
            </div>
           
            <div className="mb-2 text-white rr-label-text fw-bold mt-2">Character Description</div>
            <p className="text-muted mb-0 rr-desc-p">
              {roleData.character_sketch || req.characterDescription || '---'}
            </p>
          </div>

          <div className="card-dark">
            <div className="section-title">
              <Clapperboard size={18} className="card-header-icon text-primary me-2" />
              7. SCENE CONTEXT
            </div>
            <ul className="details-list rr-column-list mb-3">
              
             
              <li className="d-block">
                <span className="label mb-1 d-block mt-2">Key Scenes</span>
                <div className="value ps-3 text-white">
                  {roleData.story_line ? roleData.story_line.split('\n').map((line, idx) => <React.Fragment key={idx}>{line}<br/></React.Fragment>) : '---'}
                </div>
              </li>
            </ul>
            
          </div>
        </div>

        {/* ================= CENTER COLUMN ================= */}
        <div className="col-12 col-lg-4 d-flex flex-column gap-4">

          <div className="card-dark">
            <div className="section-title">
              <Users size={18} className="card-header-icon text-primary me-2" />
              2. LOOK & APPEARANCE
            </div>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {Array.isArray(lookTags) && lookTags.map((tag, idx) => (
                <span key={idx} className="badge badge-red badge-custom">{formatSemicolonString(tag)}</span>
              ))}
            </div>
            <div className="d-flex flex-wrap gap-2 mb-4">
              {Array.isArray(lookStyles) && lookStyles.map((style, idx) => (
                <span key={idx} className="badge badge-dark-fill badge-custom">{style} (Static)</span>
              ))}
            </div>
            <div className="text-muted mb-2 rr-label-text">Reference Look (Optional) (Static)</div>
            <div className="d-flex gap-2">
              <img src={req.refLookImg1 || defaultProfile} className="rr-ref-tall-img" alt="Ref 1"/>
              <img src={req.refLookImg2 || defaultProfile} className="rr-ref-tall-img" alt="Ref 2"/>
              <img src={req.refLookImg3 || defaultProfile} className="rr-ref-tall-img" alt="Ref 3"/>
              <img src={req.refLookImg4 || defaultProfile} className="rr-ref-tall-img" alt="Ref 4"/>
            </div>
          </div>

          <div className="card-dark">
            <div className="section-title">
              <Heart size={18} className="card-header-icon text-primary me-2" />
              5. EMOTIONAL REQUIREMENT
            </div>
            <div className="d-flex justify-content-between mb-3 text-muted rr-label-sub">
              <span>Emotions</span>
              <span>(Importance Level)</span>
            </div>
            
            {Array.isArray(emotions) && emotions.map(item => (
              <div className="emotion-row rr-emotion-row" key={item.label}>
                <div className="emotion-label text-muted small">
                  <span className="me-2">{item.e}</span> {item.label}
                </div>
                <div className="emotion-bar-container">
                  <div className="custom-progress rr-progress-track">
                    <div className="custom-progress-bar rr-progress-fill" style={{ width: `${item.val}%` }}></div>
                  </div>
                </div>
                <div className="emotion-value text-primary fw-bold">{item.val}%</div>
              </div>
            ))}
          </div>

          <div className="card-dark">
            <div className="section-title">
              <Shirt size={18} className="card-header-icon text-primary me-2" />
              8. COSTUME & LOOK PREFERENCE
            </div>
            <ul className="details-list rr-column-list">
              <li className="d-block mb-3">
                <span className="label mb-1 d-block">Primary Look</span>
                <span className="value text-white">{req.primaryLook || 'Formal / Corporate (Static)'}</span>
              </li>
              <li className="d-block mb-3">
                <span className="label mb-1 d-block">Costume Style</span>
                <span className="value text-white">{req.costumeStyle || 'Western Formals, Saree in courtroom, Casuals in personal scenes (Static)'}</span>
              </li>
              <li className="d-block mb-3">
                <span className="label mb-1 d-block">Avoid</span>
                <span className="value text-white">{req.avoid || 'Over glamorous, heavy makeup looks, unrealistic styling (Static)'}</span>
              </li>
              <li className="d-block mb-3">
                <span className="label mb-1 d-block">Makeup</span>
                <span className="value text-white">{req.makeup || 'Natural / Minimal (Static)'}</span>
              </li>
              <li className="d-block mb-0">
                <span className="label mb-1 d-block">Accessories</span>
                <span className="value text-white">{req.accessories || 'Minimal / Professional (Static)'}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* ================= RIGHT COLUMN ================= */}
        <div className="col-12 col-lg-4 d-flex flex-column gap-4">

          <div className="card-dark">
            <div className="section-title">
              <MessageCircle size={18} className="card-header-icon text-primary me-2" />
              3. LANGUAGE & ACCENT
            </div>
            <ul className="details-list rr-column-list mb-4">
              {roleData.required_language ? (
                roleData.required_language.split(';').map((lang, idx) => (
                  <tr key={idx}>
                    <td><span className="label">{lang.trim()}</span></td>
                    <td><span className="value text-success fw-bold">Native / Fluent</span></td>
                  </tr>
                ))
              ) : (
                <>
                  <li><span className="label">Hindi</span><span className="value text-success fw-bold">Native (Static)</span></li>
                  <li><span className="label">English</span><span className="value text-primary fw-bold">Fluent (Static)</span></li>
                </>
              )}
            </ul>
            <ul className="details-list rr-column-list">
              <li>
                <span className="label">Accent</span>
                <span className="value text-white">{req.accent || 'Neutral Hindi (Static)'}</span>
              </li>
              <li className="mt-3 d-block">
                <span className="label d-block mb-1">Dialogue Style</span>
                <span className="value text-white">{req.dialogueStyle || 'Natural & Realistic (Static)'}</span>
              </li>
              <li className="mt-3 d-block">
                <span className="label d-block mb-1">Must be able to</span>
                <span className="value text-white">{req.mustBeAbleTo || 'Deliver strong dialogues with clarity and emotion. (Static)'}</span>
              </li>
            </ul>
          </div>

          <div className="card-dark">
            <div className="section-title">
              <Star size={18} className="card-header-icon text-primary me-2" />
              6. SKILLS REQUIRED
            </div>
            <ul className="details-list rr-column-list">
              {Array.isArray(skillsRequired) && skillsRequired.map(item => (
                <li key={item.s} className="align-items-center">
                  <span className="label">{item.s}</span>
                  <span className="value text-end">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star 
                        key={i} 
                        size={14} 
                        fill={i <= item.r ? "currentColor" : "none"} 
                        className={i <= item.r ? "text-primary me-1" : "text-muted me-1"} 
                      />
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          
        </div>

        {/* ================= 10. AUDITION REQUIREMENTS ================= */}
        <div className="col-12 mt-4">
          <div className="card-dark rr-audition-card">
            <div className="section-title border-bottom border-secondary pb-2 mb-3">
              <Video size={18} className="card-header-icon text-primary me-2" />
              10. AUDITION REQUIREMENTS
            </div>
            
            <div className="row g-4">
              <div className="col-6 col-md-2">
                <div className="text-muted mb-2 rr-label-sub">Audition Type</div>
                <div className="d-flex align-items-center gap-2">
                  <Video size={16} className="text-primary"/> <span className="fw-medium text-white">{formatSemicolonString(roleData.audition_type, 'Self Tape')}</span>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="text-muted mb-2 rr-label-sub">Audition Script</div>
                <div className="d-flex align-items-center gap-2 text-primary">
                  {roleData.role_script || req.scriptUrl ? (
                    <a href={roleData.role_script || req.scriptUrl} target="_blank" rel="noreferrer" className="text-decoration-none d-flex align-items-center gap-1 text-primary">
                      <Save size={16}/> <span>Download<br/><small className="text-muted">(Script)</small></span>
                    </a>
                  ) : (
                    <>
                      <Save size={16}/> <span>Not Attached (Static)</span>
                    </>
                  )}
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="text-muted mb-2 rr-label-sub">Duration</div>
                <div className="d-flex align-items-center gap-2 text-white">
                  <Calendar size={16} className="text-primary"/> <span>{req.auditionDuration || '1 - 2 Minutes (Static)'}</span>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="text-muted mb-2 rr-label-sub">Format</div>
                <div className="d-flex align-items-center gap-2 text-white">
                  <Clapperboard size={16} className="text-primary"/> <span>{req.auditionFormat || 'MP4 (Horizontal) (Static)'}</span>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="text-muted mb-2 rr-label-sub">Self Introduction</div>
                <div className="d-flex align-items-center gap-2 text-white">
                  <User size={16} className="text-primary"/> <span>{req.selfIntroduction || 'Mandatory (Static)'}</span>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="text-muted mb-2 rr-label-sub">Deadline</div>
                <div className="d-flex align-items-center gap-2 text-white">
                  <Calendar size={16} className="text-primary"/> <span>{formattedEndDate}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-3 border-top border-secondary">
              <div className="text-muted mb-1 rr-label-sub">Notes</div>
              <p className="mb-0 text-white cp-bio">{req.notes || 'Please introduce yourself and perform the given scene with full emotion. (Static)'}</p>
            </div>
          </div>
        </div>

        {/* BOTTOM SECTIONS */}
        <div className="col-12 col-md-6 mt-4">
          <div className="card-dark h-100">
            <div className="text-primary fw-medium mb-3 d-flex align-items-center gap-2 rr-label-text">
               IMPORTANT NOTES (Static)
            </div>
            <ul className="text-muted rr-notes-list">
              <li className="mb-2">Provide clear, well-lit videos without background noise.</li>
              <li className="mb-2">Do not use heavy filters or editing.</li>
              <li className="mb-0">Focus on natural performance.</li>
            </ul>
          </div>
        </div>

        <div className="col-12 col-md-6 mt-4">
          <div className="card-dark h-100">
            <div className="text-primary fw-medium mb-3 d-flex align-items-center gap-2 rr-label-text">
               AI MATCHING FACTORS (Static)
            </div>
            <div className="d-flex flex-wrap gap-4 text-muted rr-label-sub">
              <div className="d-flex align-items-center gap-2"><User size={16} className="text-primary"/> Look Match</div>
              <div className="d-flex align-items-center gap-2"><Star size={16} className="text-primary"/> Skill Match</div>
              <div className="d-flex align-items-center gap-2"><Heart size={16} className="text-primary"/> Emotion Match</div>
              <div className="d-flex align-items-center gap-2"><MessageCircle size={16} className="text-primary"/> Language Match</div>
              <div className="d-flex align-items-center gap-2"><Video size={16} className="text-primary"/> Performance Score</div>
            </div>
          </div>
        </div>
        
      </div>
      
    </div>
  );
}
